<template>
  <ServiceContentDetail
    :pending="pending && !contentDetail"
    :error="error"
    error-title="帮助内容加载失败"
    :content-detail="contentDetail"
    :title="contentDetail?.title || ''"
    center-label="帮助中心"
    breadcrumb-current="内容详情"
    :list-route="listRoute"
    :applicable-products="applicableProducts"
    :show-share="applicableProducts.length > 0"
    :detail-resource="detailResource"
    :processed-html="processedHtml"
    :files="files"
    :downloading-map="downloadingFileMap"
    :resolve-preview-file="resolvePreviewFile"
    :is-resource-downloading="isFileDownloading(detailResource)"
    :content-id="contentId"
    :toc-items="tocItems"
    @share="handleShare"
    @preview="handleFilePreview"
    @download="handleFileDownload"
    @file-share="handleFileShare"
    @refresh-required="handleResourceRefreshRequired"
    @toc-click="scrollToAnchor"
  />
</template>

<script setup>
/**
 * @description 帮助中心详情页
 * @reason 展示帮助文章详情，包含富文本内容、文件下载、反馈等功能
 */

import { computed, onMounted, ref, watchEffect, nextTick } from 'vue'
import { BaseMessage } from '~/utils/baseMessage'
import {
  canDownloadFile,
  downloadFileByUrl,
  resolveFileType,
  resolveDownloadDisplayName,
  resolveDownloadFileName,
} from '@/utils/download'
import { websiteApi } from '@/api/website'
import ServiceContentDetail from '~/components/website/service/ServiceContentDetail.vue'
import { resolveContentDetailResource } from '~/utils/website/contentDetailResource'
import { resolveApplicableProductModels } from '~/utils/website/helpProductModels'
import { resolveLegacyServiceContentId } from '~/utils/website/legacyContentId'
import { createPublicContentSsrRequestConfig } from '~/utils/website/ssrRequestPolicy'
import { createContentDetailRefreshGate } from '~/utils/contentResource'
import { getBaseHeaderHeightByWidth } from '~/utils/theme'
import { processServiceDetailHtmlWithToc } from '~/utils/website/serviceDetailToc'

definePageMeta({
  layout: 'website',
})

const route = useRoute()
const shouldRefreshSsrContentSnapshot = import.meta.client && useNuxtApp().isHydrating
const { setSeo } = useSeo()

setSeo({
  title: '帮助详情',
  description: 'Tenda 帮助中心文章详情。',
})

const contentId = computed(() => {
  const path = route.path || ''
  const last = path.split('/').filter(Boolean).pop()
  return resolveLegacyServiceContentId(last)
})

const contentDetail = ref(null)
const detailRefreshGate = createContentDetailRefreshGate()
const downloadingFileMap = ref({})
const listRoute = computed(() => ({
  path: '/website/service/help',
  query: Object.keys(route.query).length > 0 ? { ...route.query } : undefined,
}))

const tocItems = ref([])
const processedHtml = ref('')
const viewReportedForId = ref('')
const detailResource = computed(() => resolveContentDetailResource(contentDetail.value))
const detailRichText = computed(() => {
  if (!contentDetail.value) return ''

  if (detailResource.value.hasResource) {
    return contentDetail.value.supplyNote || ''
  }

  return (
    contentDetail.value.customizeContent ||
    contentDetail.value.content ||
    contentDetail.value.supplyNote ||
    contentDetail.value.description ||
    ''
  )
})

/**
 * @description 上报内容统计（静默失败）
 * @param {string|number} id - 内容ID
 * @param {number} statType - 1 预览量 2 下载量 3 分享量 4 有帮助 5 无帮助
 */
const reportContentStat = async (id, statType) => {
  if (!id) return
  try {
    await websiteApi.updateContentStat({ contentId: id, statType })
  } catch (e) {
    // 静默失败，不影响用户操作
  }
}

watchEffect(() => {
  if (!contentDetail.value) {
    tocItems.value = []
    processedHtml.value = ''
    return
  }

  const rawHtml = detailRichText.value
  if (rawHtml) {
    const { html, tocItems: items } = processServiceDetailHtmlWithToc(rawHtml)
    processedHtml.value = html
    tocItems.value = items
  } else {
    tocItems.value = []
    processedHtml.value = ''
  }
})

/**
 * @description 获取适用产品型号列表，型号缺失时回退商品名
 */
const applicableProducts = computed(() => {
  return resolveApplicableProductModels(contentDetail.value)
})

/**
 * @description 获取文件列表
 */
const files = computed(() => {
  if (!contentDetail.value) return []

  const attachmentList = contentDetail.value.attachmentInfoList || contentDetail.value.files || []

  return attachmentList.map((file) => {
    const normalizedFile = {
      ...file,
      title: file.title || file.fileTitle || '',
      fileName: file.name || file.fileName,
      name: file.name || file.fileName,
      fileUrl: file.fileUrl || file.previewUrl || file.url,
      url: file.url,
      downloadUrl: file.fileUrl || file.previewUrl || file.downloadUrl || file.url,
      fileSize: file.size || file.fileSize,
      size: file.size || file.fileSize,
      createTime: file.createTime,
      updateTime: file.updateTime,
      contentId: file.contentId,
    }
    return {
      ...normalizedFile,
      displayName: resolveDownloadDisplayName({
        title: file.title || file.fileTitle,
        name: file.name,
        fileName: file.fileName,
      }),
      fileType: resolveFileType(normalizedFile),
    }
  })
})

/**
 * @description 生成附件下载记录的稳定键值，用于按钮级 loading 状态管理
 * @param {Object} file - 文件对象
 * @returns {string}
 */
const getFileDownloadKey = (file) => {
  return String(
    file?.contentId ||
      file?.fileUrl ||
      file?.url ||
      file?.downloadUrl ||
      file?.fileName ||
      file?.name ||
      ''
  )
}

/**
 * @description 判断指定文件是否正在下载
 * @param {Object} file - 文件对象
 * @returns {boolean}
 */
const isFileDownloading = (file) => {
  return Boolean(downloadingFileMap.value[getFileDownloadKey(file)])
}

/**
 * @description 滚动到锚点
 * @param {string} anchorId - 锚点ID
 */
/**
 * @description 滚动到锚点
 * @param {string} anchorId - 锚点ID
 */
const scrollToAnchor = (anchorId) => {
  if (!import.meta.client) return

  nextTick(() => {
    const element = document.getElementById(anchorId)
    if (element) {
      const headerOffset =
        document.querySelector('.base-header')?.getBoundingClientRect().height ||
        getBaseHeaderHeightByWidth(window.innerWidth)
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })

      if (window.history && window.history.pushState) {
        window.history.pushState(null, '', `#${anchorId}`)
      }
    }
  })
}

/**
 * @description 处理分享
 */
const handleShare = async () => {
  reportContentStat(contentId.value, 3)
  const shouldSkipNativeShare = true
  if (shouldSkipNativeShare) return
  if (!import.meta.client) return
  const shareUrl = window.location.href

  try {
    if (navigator.share) {
      await navigator.share({
        title: contentDetail.value?.title || '帮助详情',
        url: shareUrl,
      })
      return
    }

    await navigator.clipboard.writeText(shareUrl)
    BaseMessage.success('链接已复制')
  } catch (e) {
    BaseMessage.error('分享失败，请稍后重试')
  }
}

const handleFileShare = (file) => {
  reportContentStat(file?.contentId || contentId.value, 3)
}

const handleFilePreview = (file) => {
  reportContentStat(file?.contentId || contentId.value, 1)
}

/**
 * @description 处理文件下载，并上报文件下载量
 * @param {Object} file - 文件对象
 */
const handleFileDownload = async (file) => {
  if (!canDownloadFile(file)) return

  const downloadKey = getFileDownloadKey(file)
  if (downloadingFileMap.value[downloadKey]) return

  downloadingFileMap.value = {
    ...downloadingFileMap.value,
    [downloadKey]: true,
  }

  try {
    await refresh()
    const freshFile = findCurrentFile(file)
    const fileUrl = freshFile.fileUrl || freshFile.downloadUrl || freshFile.url
    if (!fileUrl) throw new Error('File URL is missing')
    const result = await downloadFileByUrl({
      url: fileUrl,
      filename: resolveDownloadFileName(freshFile),
      fileType: freshFile.fileType,
      contentForm: freshFile.contentForm,
    })
    BaseMessage.success(result?.strategy === 'native' ? '已开始下载' : '保存成功')
    reportContentStat(file.contentId || contentId.value, 2)
  } catch (error) {
    BaseMessage.error('下载失败，请稍后重试')
  } finally {
    const nextMap = { ...downloadingFileMap.value }
    delete nextMap[downloadKey]
    downloadingFileMap.value = nextMap
  }
}

const {
  data: rawContentDetail,
  pending,
  error,
  refresh,
} = await useAsyncData(
  () => `help-content-detail-${contentId.value}`,
  async () => {
    const id = contentId.value
    if (!id) return null
    try {
      const response = import.meta.server
        ? await websiteApi.getContentDetail(id, createPublicContentSsrRequestConfig())
        : await websiteApi.getContentDetail(id)
      return response?.data || response || null
    } catch (e) {
      if (import.meta.dev) return null
      console.error('获取内容详情失败:', e)
      throw e
    }
  },
  usePublicContentAsyncDataOptions({
    default: () => null,
    watch: [contentId],
  })
)

const handleResourceRefreshRequired = (request) => {
  if (request && !detailRefreshGate.shouldRequest({ contentId: contentId.value, ...request }))
    return
  refresh().catch(() => {})
}

const findCurrentFile = (file) => {
  return (
    files.value.find(
      (candidate) =>
        (file?.resourceId && candidate.resourceId === file.resourceId) ||
        (!file?.resourceId && candidate.fileName === file?.fileName)
    ) || file
  )
}

const resolvePreviewFile = async (file) => {
  await refresh()
  await nextTick()
  return findCurrentFile(file)
}

onMounted(() => {
  if (shouldRefreshSsrContentSnapshot) {
    refresh().catch(() => {})
  }
})

watchEffect(() => {
  const raw = rawContentDetail.value
  if (raw && typeof raw === 'object') {
    contentDetail.value = raw
    return
  }
  contentDetail.value = null
})

watchEffect(() => {
  const d = contentDetail.value
  if (!d) return

  setSeo({
    title: d.title,
    description: d.abstractText || d.abstract || d.title,
  })
})

watchEffect(() => {
  if (!import.meta.client) return
  const id = contentId.value
  if (!id || viewReportedForId.value === id) return
  viewReportedForId.value = id
  reportContentStat(id, 1)
})
</script>
